import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';
import { environment } from 'environments/environment';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { toOAuthTokens } from '../shared/authentication';
import type { OAuthTokenResponse } from '../shared/authentication.types';
import {
  authorizationRedirectUri,
  clearPendingAuthorizationRequest,
  readPendingAuthorizationRequest,
} from '../shared/authorization-request';
import { LandingResolverService } from '../shared/landing-resolver.service';
import { TokenStore } from '../shared/token-store.service';

/**
 * `/auth/callback` of the local strategy: redeems the authorization code with the PKCE verifier
 * kept in `sessionStorage`. The satellite `/callback` has its own guard (BFF exchange).
 *
 * The pending request is deleted on every outcome, and a failure goes to `/callback-error`
 * WITHOUT restarting the request, so a failing exchange can never loop.
 */
export const localCallbackGuard: CanActivateFn = (route) => {
  const http = inject(HttpClient);
  const router = inject(Router);
  const tokenStore = inject(TokenStore);
  const landingResolver = inject(LandingResolverService);

  const failure = () => router.createUrlTree(['/callback-error']);

  const pending = readPendingAuthorizationRequest();
  clearPendingAuthorizationRequest();

  const code = route.queryParamMap.get('code');
  if (!pending || !code || pending.state !== route.queryParamMap.get('state')) {
    return of(failure());
  }

  const body = new HttpParams({
    fromObject: {
      grant_type: 'authorization_code',
      code,
      redirect_uri: authorizationRedirectUri(),
      code_verifier: pending.codeVerifier,
      client_id: environment.oAuth.applicationCode,
    },
  });

  return http.post<OAuthTokenResponse>(`${environment.api.rest}/o-auth/token`, body).pipe(
    switchMap((response) => {
      tokenStore.setCredentials(toOAuthTokens(response));
      // The landing is returned as this guard's redirect, never navigated on the side: that would
      // cancel this navigation, and a landing equal to the current URL would be ignored as a
      // same-URL navigation, leaving a blank page. replaceUrl keeps /auth/callback out of history,
      // since its one-time code cannot be redeemed twice.
      return from(landingResolver.resolve(pending.redirectURL)).pipe(
        map((route) => new RedirectCommand(router.parseUrl(route), { replaceUrl: true })),
      );
    }),
    catchError(() => of(failure())),
  );
};
